/**
 * useEngagementEntries — shared hook that produces the enriched
 * engagement/role list (the unified SelectableEntry[]) consumed by the
 * Engagements page left pane and the Action Center role rail
 * (SPEC_ROLE_ORGANIZED_INBOX §4a).
 *
 * EXTRACTION-BY-COPY from EngagementsPage.tsx — the lookups, enrichRoles,
 * myCommunityEngagements, myWorkspaces, and selectableEntries build are
 * reproduced faithfully here so both surfaces share one source of truth.
 * EngagementsPage converges onto this hook in a later stage; until then
 * the page keeps its own inline copy and this file is verified standalone.
 *
 * The inputs EngagementsPage receives as props are taken here as `opts`
 * arguments so both consumers can supply them; the three async role/MP/
 * occupancy hooks are called internally (same as the page).
 */
import { useMemo, useState, useCallback, useEffect } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useApiUserRoles } from '../../api/useApiUserRoles';
import { useApiMiniProjects } from '../../api/useApiMiniProjects';
import { useApiCommunitySlotOccupancies } from '../../api/useApiCommunitySlotOccupancies';
import { useApiCommunityPreviousRoles } from '../../api/useApiCommunityPreviousRoles';
import { resolveEffectiveLogoUrl } from '../../api/communityMetadata'; // DEV-895 — owner avatar = logo, not hero
import { isOrgEntityType } from '../../types/entityTypes'; // DEV-1164 follow-up — canonical org-vs-geo predicate
import type { UserRoleAssignmentResponse } from '../../api/impactSignature';
import { mediaSrc } from '../../api/mediaUrl'; // single tolerant image-src resolver
import { formatCommunityPathLabel } from '../../Data/ProjectConfig/projectConfigOverrides';
import { fetchWorkspaces, type WorkspaceResponse } from '../../api/workspaces';
import type { SelectableEntry } from './engagementEntry';

/* ------------------------------------------------------------------ */
/*  Options                                                            */
/* ------------------------------------------------------------------ */

export interface UseEngagementEntriesOptions {
  currentUserGuid: string;
  projectData?: any[];
  /** Hero-image lookup from the unfiltered project set (guid→url). Resolves thumbnails for projects the user has roles on but can't see in the main grid. */
  allProjectHeroLookup?: Map<string, string>;
  users?: any[];
  communities?: any[];
  projectAcl?: any[];
  communityRoleAssignments?: any[];
  /** DEV-895 — needed for resolveEffectiveLogoUrl ancestor cascade. */
  communityRelationships?: any[];
}

export interface UseEngagementEntriesResult {
  entries: SelectableEntry[];
  loading: boolean;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

interface ResolvedUser {
  name: string;
  avatarUrl: string | null;
}

function resolveAvatarUrl(raw: string | undefined | null): string | null {
  // Delegates to the shared tolerant resolver so every stored format works.
  return mediaSrc(raw);
}

function buildUserLookup(users: any[]): Map<string, ResolvedUser> {
  const map = new Map<string, ResolvedUser>();
  if (!users) return map;
  for (const u of users) {
    const guid = String(u.user_guid || u.userGuid || '').toUpperCase();
    if (!guid) continue;
    map.set(guid, {
      name: u.display_name || u.displayName || u.name || '',
      avatarUrl: resolveAvatarUrl(u.profile_image_url || u.profileImageUrl),
    });
  }
  return map;
}

function buildCommunityLookup(communities: any[]): Map<string, { name: string; guid: string }> {
  const map = new Map<string, { name: string; guid: string }>();
  if (!communities) return map;
  for (const c of communities) {
    const guid = String(c.entity_guid || c.entityGuid || '').toUpperCase();
    if (!guid) continue;
    map.set(guid, { name: c.name || '', guid: String(c.entity_guid || c.entityGuid || '') });
  }
  return map;
}

function buildOwnerLookup(acl: any[]): Map<string, string> {
  const map = new Map<string, string>();
  if (!acl) return map;
  for (const entry of acl) {
    if (String(entry.role) !== 'owner_admin') continue;
    if (entry.is_active === false) continue;
    const pg = String(entry.project_guid || '').toUpperCase();
    const ug = String(entry.user_guid || '').toUpperCase();
    if (pg && ug) map.set(pg, ug);
  }
  return map;
}

/* ------------------------------------------------------------------ */
/*  Local source interfaces (copied from EngagementsPage)             */
/* ------------------------------------------------------------------ */

interface EnrichedRole extends UserRoleAssignmentResponse {
  _projectRow: any;
  _routeGuid: string;
  _lead: ResolvedUser | null;
  _leadGuid: string | null;
  _communityName: string;
  _communityGuid: string;
  _heroUrl: string | null;
  /**
   * DEV-1102 Phase 2 — provenance of the hero photo, used by the
   * right pane's "Hero inherited from {community}" caption.
   *
   *   'own'              — hero comes from the entity itself
   *   'parent-community' — hero falls back to the entity's parent community
   *   'none'             — no hero available; right pane renders gradient
   */
  _heroSource: 'own' | 'parent-community' | 'none';
  /**
   * DEV-1102 Phase 2 follow-up — name + guid of the community or
   * organization that OWNS the project. Distinct from _communityName,
   * which is the project's physical LOCATION.
   */
  _owningCommunityName: string | null;
  _owningCommunityGuid: string | null;
}

interface CommunityEngagement {
  key: string;          // unique key for React
  entityGuid: string;   // community entity_guid (original casing for routes)
  name: string;
  role: string;         // e.g. "owner", "admin", "member"
  heroUrl: string | null;
  /**
   * DEV-1164. Tier-1 catalog linkage when this engagement is a
   * community_role_slots occupancy linked via source_definition_guid.
   */
  sourceDefinitionGuid: string | null;
  /**
   * DEV-1164 fix. The community entity's type prefix (e.g.,
   * "geo_country", "org_company", "org_rotary_club").
   */
  entityType: string;
  /**
   * DEV-1193 follow-up: active community_role_slot_assignments.assignment_guid
   * for this engagement when it surfaced as a slot occupancy.
   */
  slotAssignmentGuid: string | null;
  /**
   * DEV-1224 follow-up (2026-06-03): the community_role_slot.role_slot_guid
   * for this engagement when it surfaced as a slot occupancy.
   */
  roleSlotGuid: string | null;
  /**
   * DEV-1194. Resolved community / organization logo URL with ancestor
   * cascade — entity's own logo → nearest ancestor with a logo → null.
   */
  logoUrl: string | null;
  /**
   * DEV-1368. TRUE when this is a role the user PREVIOUSLY held (terminal
   * community_role_slot_assignments, status revoked/removed). Hidden from
   * the rail unless "Show previous roles" is on; rendered muted with a
   * "Former" pill. Communities persist, so the previousKind is always
   * "role_ended" — there is no "community ended" analog.
   */
  isPrevious?: boolean;
}

interface WorkspaceEngagement {
  key: string;
  workspaceGuid: string;
  title: string;
  role: string;
  phase: string;
  heroUrl: string | null;
  communityGuid: string | null;
  communityName: string;
  teamLeadGuid: string;
  leadUser: ResolvedUser | null;
}

/* ------------------------------------------------------------------ */
/*  Hook                                                               */
/* ------------------------------------------------------------------ */

export function useEngagementEntries(
  opts: UseEngagementEntriesOptions,
): UseEngagementEntriesResult {
  const {
    currentUserGuid,
    projectData,
    allProjectHeroLookup,
    users,
    communities,
    projectAcl,
    communityRoleAssignments,
    communityRelationships,
  } = opts;

  const { t } = useAppTranslation('pages');

  const { activeRoles, completedRoles, loading: rolesLoading } = useApiUserRoles(currentUserGuid);

  const { occupancies: communitySlotOccupancies } =
    useApiCommunitySlotOccupancies(currentUserGuid);

  // DEV-1368 — community/org roles the user previously held (terminal
  // revoked/removed assignments). Surfaced behind "Show previous roles".
  const { previousRoles: communityPreviousRoles } =
    useApiCommunityPreviousRoles(currentUserGuid);

  /* ── Lookups ── */

  const projectLookup = useMemo(() => {
    const map = new Map<string, any>();
    if (!projectData) return map;
    for (const row of projectData) {
      const guid = String(row?.project_guid || row?.project_GUID || '').trim().toUpperCase();
      if (guid) map.set(guid, row);
    }
    return map;
  }, [projectData]);

  // DEV-1088 — Mini-Projects come from useApiMiniProjects, not from the
  // Data prop. Build a parallel lookup so enrichRoles can hydrate
  // hero / location / owner for engagementType === 'mini-project' rows.
  const { miniProjects, loading: miniProjectsLoading } = useApiMiniProjects();
  const miniProjectLookup = useMemo(() => {
    const map = new Map<string, any>();
    if (!miniProjects) return map;
    for (const mp of miniProjects) {
      const guid = String(mp.miniProjectGuid || '').trim().toUpperCase();
      if (guid) map.set(guid, mp);
    }
    return map;
  }, [miniProjects]);

  const userLookup = useMemo(() => buildUserLookup(users || []), [users]);
  const communityLookup = useMemo(() => buildCommunityLookup(communities || []), [communities]);
  const ownerLookup = useMemo(() => buildOwnerLookup(projectAcl || []), [projectAcl]);

  /* ── Enrich roles ── */

  const enrichRoles = useCallback((roles: UserRoleAssignmentResponse[]): EnrichedRole[] => {
    if (!roles || roles.length === 0) return [];
    return roles.map((r) => {
      const pguid = (r.projectGuid || '').toUpperCase();
      const isMini = r.engagementType === 'mini-project';

      // DEV-1088 — Mini-Project rows live in a separate lookup. The
      // shape differs from the master Data row (camelCase, fewer
      // fields), so we normalize into a Data-row-like surface here so
      // the rest of the enrichment logic below can read the same keys
      // (`primary_location_entity_guid`, `card_image_url`, …) without
      // a second branch in every line.
      const miniRow = isMini ? miniProjectLookup.get(pguid) : undefined;
      const dataRow = isMini
        ? miniRow
          ? {
              project_guid: miniRow.miniProjectGuid,
              project: {
                primary_location_entity_guid: miniRow.locationGeographyGuid,
                card_image_url: miniRow.heroImageUrl,
                hero_image_url: miniRow.heroImageUrl,
              },
            }
          : undefined
        : projectLookup.get(pguid);
      const project = dataRow?.project || dataRow || {};

      const routeGuid = String(
        dataRow?.project_guid || dataRow?.project_GUID || r.projectGuid || ''
      );

      // For Mini-Projects the Lead is in mini_projects.owner_user_guid,
      // which doesn't flow through projectAcl (ownerLookup). Resolve
      // directly off the miniRow.
      const ownerGuid = isMini
        ? (miniRow?.ownerUserGuid ? String(miniRow.ownerUserGuid).toUpperCase() : null)
        : (ownerLookup.get(pguid) || null);
      const lead = ownerGuid ? userLookup.get(ownerGuid) || null : null;

      const locGuid = String(project.primary_location_entity_guid || '').toUpperCase();
      const community = locGuid ? communityLookup.get(locGuid) : undefined;
      const communityGuid = community?.guid || String(project.primary_location_entity_guid || '');
      // DEV-1025 — render the full disambiguating path ("Newport, Rhode
      // Island, United States") rather than the bare leaf name so users
      // can tell which "Newport" a project belongs to at a glance.
      const communityName =
        formatCommunityPathLabel(communityGuid) ||
        community?.name ||
        project.Community ||
        '';

      // Resolve hero: prefer card-specific image, then main hero from the
      // filtered project data. Fall back to the unfiltered allProjectHeroLookup
      // so engagement cards show thumbnails even for projects the user can't
      // see in the main grid (e.g. when simulating another user).
      // DEV-1102 Phase 2 follow-up — hero_image_url first so the right-pane
      // banner shows the detail-page hero (the wide, banner-style image) and
      // not the card thumbnail. card_image_url stays as a fallback for
      // projects that haven't set a dedicated hero yet. For mini-projects
      // both fields map to the same `miniRow.heroImageUrl` so this priority
      // change is a no-op for them.
      const heroRaw = project.hero_image_url || project.card_image_url || project.tile_image_url
        || allProjectHeroLookup?.get(pguid) || '';
      const heroUrl = heroRaw ? resolveAvatarUrl(heroRaw) : null;
      // DEV-1102 Phase 2 — see _heroSource doc on EnrichedRole. Phase 2
      // can only distinguish 'own' from 'none' because no parent-community
      // fallback exists yet.
      const heroSource: 'own' | 'none' = heroUrl ? 'own' : 'none';

      // DEV-1102 Phase 2 follow-up — resolve owning community.
      // Mini-projects: always community-owned per schema, off
      // miniRow.ownerCommunityGuid (camelCase on the MP API).
      // Full projects: owner_type === 'community' && owner_guid, with
      // legacy owning_community_entity_guid as a fallback (see
      // _owningCommunityName doc on EnrichedRole for rationale).
      let owningCommunityGuid = '';
      if (isMini) {
        owningCommunityGuid = String(miniRow?.ownerCommunityGuid || '').toUpperCase();
      } else {
        const ownerType = String(project.owner_type || '').toLowerCase();
        if (ownerType === 'community' && project.owner_guid) {
          owningCommunityGuid = String(project.owner_guid).toUpperCase();
        } else if (project.owning_community_entity_guid) {
          owningCommunityGuid = String(project.owning_community_entity_guid).toUpperCase();
        }
      }

      let owningCommunityName = '';
      if (owningCommunityGuid) {
        const owningCommunity = communityLookup.get(owningCommunityGuid);
        // Use the disambiguated path label when available (consistent
        // with the _communityName treatment per DEV-1025). Fall back to
        // the bare lookup name when the path resolver returns nothing.
        const guidForLabel = owningCommunity?.guid || owningCommunityGuid;
        owningCommunityName =
          formatCommunityPathLabel(guidForLabel) || owningCommunity?.name || '';
      }

      return {
        ...r,
        _projectRow: project,
        _routeGuid: routeGuid,
        _lead: lead,
        _leadGuid: ownerGuid || null,
        _communityName: communityName,
        _communityGuid: communityGuid,
        _heroUrl: heroUrl,
        _heroSource: heroSource,
        _owningCommunityName: owningCommunityName || null,
        _owningCommunityGuid: owningCommunityGuid || null,
      };
    });
  }, [projectLookup, miniProjectLookup, userLookup, communityLookup, ownerLookup, allProjectHeroLookup]);

  const enrichedRoles: EnrichedRole[] = useMemo(
    () => enrichRoles(activeRoles), [activeRoles, enrichRoles]
  );
  // DEV-1368 — completed/ended project role assignments (already fetched by
  // useApiUserRoles, previously unused). Surfaced as "previous" roles behind
  // the rail's "Show previous roles" toggle.
  const enrichedCompleted: EnrichedRole[] = useMemo(
    () => enrichRoles(completedRoles), [completedRoles, enrichRoles]
  );

  /* ── Community engagement rows ── */

  const myCommunityEngagements: CommunityEngagement[] = useMemo(() => {
    const ug = (currentUserGuid || '').toUpperCase();
    if (!ug) return [];
    const seen = new Set<string>(); // dedup by entityGuid+role
    const rows: CommunityEngagement[] = [];

    // Only from communityRoleAssignments — but NOT the "owner" role.
    // Owner-only relationships belong on Lead → Communities, not here.
    // Plain geographic community membership does NOT constitute an engagement.
    if (communityRoleAssignments) {
      for (const r of communityRoleAssignments) {
        const userGuid = String(r.user_guid || r.userGuid || '').toUpperCase();
        if (userGuid !== ug) continue;
        const role = String(r.role || '');
        // Skip ownership — it belongs on the Lead page, not Engagements
        if (role === 'owner') continue;
        const eg = String(r.community_entity_guid || r.communityEntityGuid || '').toUpperCase();
        const dedupKey = `${eg}:${role}`;
        if (seen.has(dedupKey)) continue;
        seen.add(dedupKey);
        const comm = communityLookup.get(eg);
        // Find hero image from communities array
        const commEntity = (communities || []).find(
          (c: any) => String(c.entity_guid || c.entityGuid || '').toUpperCase() === eg
        );
        const heroRaw = commEntity?.hero_image_url || commEntity?.heroImageUrl || '';
        // DEV-1194 — logo for the detail-header avatar. Uses the canonical
        // resolveEffectiveLogoUrl ancestor cascade when relationships are
        // loaded (Newport Rotary Club without its own logo borrows from
        // Rotary International; a small geo city borrows the country flag),
        // falling back to the entity's own logo_url otherwise.
        const ownGuid = String(r.community_entity_guid || r.communityEntityGuid || '');
        const logoRaw = communityRelationships
          ? resolveEffectiveLogoUrl(ownGuid, communities || [], communityRelationships)
          : (commEntity?.logo_url || commEntity?.logoUrl || null);
        const eGuidForLabel = comm?.guid || ownGuid;
        rows.push({
          key: `cr-${eg}-${role}`,
          entityGuid: eGuidForLabel,
          // DEV-1025 — show the full path so geo-community engagement tiles
          // disambiguate (e.g. "Newport, Rhode Island, United States").
          name: formatCommunityPathLabel(eGuidForLabel) || comm?.name || '',
          role,
          heroUrl: heroRaw ? resolveAvatarUrl(heroRaw) : null,
          // DEV-1164 — plain V1 role assignments don't carry a Tier-1
          // catalog linkage; only slot occupancies (below) do.
          sourceDefinitionGuid: null,
          // DEV-1164 fix — entity type for org-vs-geo typeTag resolution.
          entityType: String(commEntity?.entity_type || commEntity?.entityType || ''),
          // DEV-1193 follow-up — V1 memberships have no slot
          // assignment; resign falls through to the V1 grant path.
          slotAssignmentGuid: null,
          // DEV-1224 follow-up — V1 memberships have no slot anchor,
          // so the year-at-a-glance history fetch is skipped.
          roleSlotGuid: null,
          // DEV-1194 — detail-header avatar for the community engagement.
          logoUrl: logoRaw ? resolveAvatarUrl(logoRaw) : null,
        });
      }
    }

    // DEV-1145 Phase 1j — community_role_slots occupancies (Club Secretary,
    // Mayor, Town Manager, custom roles, etc.). Filters out the
    // Lead/Officer/Observer default slots — those duplicate the
    // V1 owner/admin/member rows above and add no information.
    // Hidden slots are also filtered (POLICY_TEAM_ROSTERS migration 171:
    // is_hidden is for public-facing display only, but on this user-private
    // Engagements page it would still surface; we honor it as a privacy
    // setting nonetheless — admins manage their own hidden state).
    for (const s of communitySlotOccupancies) {
      // Default slots (Lead/Officer/Observer) are filtered as V1 dupes ONLY
      // when they're legacy auto-created occupancies (no assignment). A default
      // slot the user EXPLICITLY accepted via the invitation flow carries an
      // assignmentGuid and is a real engagement — show it (DEV-1368, Skip
      // 2026-06-23: accepting "Lead" on Rotary International vanished otherwise).
      if (s.isDefaultSlot && !s.assignmentGuid) continue;
      if (s.isHidden) continue;             // privacy: respect hidden flag
      const eg = s.communityEntityGuid.toUpperCase();
      const role = s.displayTitle || 'Team member';
      const dedupKey = `${eg}:slot:${s.roleSlotGuid}`;
      if (seen.has(dedupKey)) continue;
      seen.add(dedupKey);
      const comm = communityLookup.get(eg);
      const commEntity = (communities || []).find(
        (c: any) => String(c.entity_guid || c.entityGuid || '').toUpperCase() === eg
      );
      const heroRaw = commEntity?.hero_image_url || commEntity?.heroImageUrl || '';
      // DEV-1194 — see matching block in the V1 assignment loop above.
      const logoRaw = communityRelationships
        ? resolveEffectiveLogoUrl(s.communityEntityGuid, communities || [], communityRelationships)
        : (commEntity?.logo_url || commEntity?.logoUrl || null);
      const eGuidForLabel = comm?.guid || s.communityEntityGuid;
      rows.push({
        key: `slot-${s.roleSlotGuid}`,
        entityGuid: eGuidForLabel,
        name: formatCommunityPathLabel(eGuidForLabel) || comm?.name || s.communityName,
        role,
        heroUrl: heroRaw ? resolveAvatarUrl(heroRaw) : null,
        // DEV-1164 — slot occupancies carry the Tier-1 catalog linkage,
        // letting the right-pane RhythmDisplayPanel resolve the rhythm.
        sourceDefinitionGuid: s.sourceDefinitionGuid ?? null,
        // DEV-1164 fix — entity type for org-vs-geo typeTag resolution.
        entityType: String(commEntity?.entity_type || commEntity?.entityType || ''),
        // DEV-1193 follow-up — surface the active assignment so Resign
        // can call /api/community-role-slot-invitations/{guid}/resign
        // instead of the V1 grant flow (which has no matching grant
        // for slot occupancy and would fail with "Could not find the
        // V2 grant for this community role").
        slotAssignmentGuid: s.assignmentGuid ?? null,
        // DEV-1224 follow-up — surface the slot guid so
        // RhythmDisplayPanel can fetch this slot's year-at-a-glance
        // instance history (status glyphs on past cadence dots).
        roleSlotGuid: s.roleSlotGuid ?? null,
        // DEV-1194 — detail-header avatar for the community engagement.
        logoUrl: logoRaw ? resolveAvatarUrl(logoRaw) : null,
      });
    }

    // DEV-1368 — PREVIOUS community/org roles (terminal revoked/removed
    // assignments). Added AFTER the current-occupancy loop and sharing the
    // same `seen` set so a re-joined slot keeps its current entry and never
    // also shows a ghost "Former" row. The backend already excludes slots
    // the user actively occupies; the seen-guard covers the same slot
    // surfacing as both a V1 assignment and a previous slot row.
    for (const p of communityPreviousRoles) {
      if (p.isHidden) continue;             // privacy: respect hidden flag
      const eg = p.communityEntityGuid.toUpperCase();
      const role = p.displayTitle || 'Team member';
      const dedupKey = `${eg}:slot:${p.roleSlotGuid}`;
      if (seen.has(dedupKey)) continue;
      seen.add(dedupKey);
      const comm = communityLookup.get(eg);
      const commEntity = (communities || []).find(
        (c: any) => String(c.entity_guid || c.entityGuid || '').toUpperCase() === eg
      );
      const heroRaw = commEntity?.hero_image_url || commEntity?.heroImageUrl || '';
      const logoRaw = communityRelationships
        ? resolveEffectiveLogoUrl(p.communityEntityGuid, communities || [], communityRelationships)
        : (commEntity?.logo_url || commEntity?.logoUrl || null);
      const eGuidForLabel = comm?.guid || p.communityEntityGuid;
      rows.push({
        key: `slot-prev-${p.roleSlotGuid}`,
        entityGuid: eGuidForLabel,
        name: formatCommunityPathLabel(eGuidForLabel) || comm?.name || p.communityName,
        role,
        heroUrl: heroRaw ? resolveAvatarUrl(heroRaw) : null,
        sourceDefinitionGuid: p.sourceDefinitionGuid ?? null,
        entityType: String(commEntity?.entity_type || commEntity?.entityType || p.entityType || ''),
        // Ended role — no active assignment, so no Resign action.
        slotAssignmentGuid: null,
        roleSlotGuid: p.roleSlotGuid ?? null,
        logoUrl: logoRaw ? resolveAvatarUrl(logoRaw) : null,
        isPrevious: true,
      });
    }

    return rows;
  }, [currentUserGuid, communityRoleAssignments, communitySlotOccupancies, communityPreviousRoles, communityLookup, communities, communityRelationships]);

  /* ── Problem workspace rows ── */

  const [myWorkspaces, setMyWorkspaces] = useState<WorkspaceEngagement[]>([]);
  const [wsLoading, setWsLoading] = useState(true);

  useEffect(() => {
    if (!currentUserGuid) { setWsLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchWorkspaces({ userGuid: currentUserGuid, isActive: true });
        if (cancelled) return;
        const ug = currentUserGuid.toUpperCase();
        const rows: WorkspaceEngagement[] = data.map((ws: WorkspaceResponse) => {
          const isLead = (ws.teamLeadGuid || '').toUpperCase() === ug;
          const isCreator = (ws.createdBy || '').toUpperCase() === ug;
          const role = isLead ? 'lead' : isCreator ? 'owner' : 'investigator';
          const cg = (ws.communityGuid || '').toUpperCase();
          const comm = cg ? communityLookup.get(cg) : undefined;
          const heroRaw = ws.heroImageUrl || ws.detailHeroImageUrl || '';
          const leadGuid = (ws.teamLeadGuid || '').toUpperCase();
          return {
            key: `ws-${ws.workspaceGuid}`,
            workspaceGuid: ws.workspaceGuid,
            title: ws.title || ws.presentingConcern || '',
            role,
            phase: ws.phase || '',
            heroUrl: heroRaw ? resolveAvatarUrl(heroRaw) : null,
            communityGuid: ws.communityGuid,
            // DEV-1025 — full path on workspace tiles too.
            communityName:
              (ws.communityGuid && formatCommunityPathLabel(ws.communityGuid)) ||
              comm?.name ||
              '',
            teamLeadGuid: ws.teamLeadGuid,
            leadUser: leadGuid ? (userLookup.get(leadGuid) || null) : null,
          };
        });
        setMyWorkspaces(rows);
      } catch {
        // Workspace API may not be available; fail silently with empty list
        if (!cancelled) setMyWorkspaces([]);
      } finally {
        if (!cancelled) setWsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUserGuid, communityLookup, userLookup]);

  /* ── Split into pillar buckets ── */

  const projectRoles = enrichedRoles;
  // DEV-1102 Phase 2 — placeholders memoized so they're stable references,
  // otherwise selectableEntries' useMemo deps churn every render and
  // react-hooks/exhaustive-deps complains. When the Solutions API and
  // the Enablers source(s) land — organizations are one Enabler entity
  // type, content-on-behalf-of roles will be another — replace the
  // empty literal with the real mapped data (still inside useMemo).
  const solutionRoles: EnrichedRole[] = useMemo(() => [], []);
  const enablerRoles: EnrichedRole[] = useMemo(() => [], []);

  /** Returns true if `iso` parses to a timestamp ≤ 3 days ago. Defensive against null/invalid input. */
  const isWithinLast3Days = (iso: string | null | undefined): boolean => {
    if (!iso) return false;
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return false;
    return Date.now() - t <= 3 * 24 * 60 * 60 * 1000;
  };

  /** Title-case a single word (e.g. "investigator" → "Investigator"). */
  const capitalize = (s: string): string =>
    s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1);

  /** Pretty-print a workspace phase enum (e.g. "evidence_gathering" → "Evidence Gathering"). */
  const prettyPhase = (phase: string): string =>
    phase.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  /**
   * Build the flat list of selectable entries from all five sources.
   * Order matters: this list is consumed by the left pane in the order
   * pillars are rendered (projects → enablers → communities → problems
   * → solutions per PILLAR_RENDER_ORDER), and the default-selection
   * effect picks the first entry of the first non-empty pillar in this
   * same order.
   */
  const selectableEntries = useMemo<SelectableEntry[]>(() => {
    // Built without the group-community fields; the post-process map below adds
    // them to produce the full SelectableEntry.
    const out: Omit<SelectableEntry, "groupCommunityGuid" | "groupCommunityName">[] = [];

    // PROBLEMS — workspaces. WorkspaceEngagement has no createdAt today,
    // so isNew is always false. Surface that limitation in a follow-up
    // if NEW pills on workspaces become valuable.
    for (const ws of myWorkspaces) {
      out.push({
        key: `ws:${ws.workspaceGuid}`,
        pillar: 'problems',
        roleLabel: capitalize(ws.role),
        entityName: ws.title,
        typeTag: 'workspace',
        secondary: prettyPhase(ws.phase),
        isNew: false,
        heroUrl: ws.heroUrl,
        heroSource: ws.heroUrl ? 'own' : 'none',
        logoUrl: null, // workspaces use the square hero thumbnail
        // Workspaces carry a team lead, not an owning community.
        owningCommunityName: null,
        owningCommunityGuid: null,
        source: { type: 'workspace', data: ws },
      });
    }

    // SOLUTIONS — placeholder (solutionRoles is currently always empty
    // pending the Solutions API; see also Phase 6 empty-state polish).
    for (const r of solutionRoles) {
      out.push({
        key: `role:${r.assignmentGuid}`,
        pillar: 'solutions',
        roleLabel: r.roleLabel || r.roleCategory || '',
        entityName: r.projectName || t('engagements.unknownProject'),
        typeTag: 'full-project',
        // DEV-1164 iter 3 cleanup — time commitment scrubbed from card
        // secondary per Skip's feedback; it lives on the About tab.
        secondary: '',
        isNew: isWithinLast3Days(r.assignedAt),
        heroUrl: r._heroUrl,
        heroSource: r._heroSource,
        logoUrl: null, // project/enabler roles use the square hero thumbnail
        owningCommunityName: r._owningCommunityName,
        owningCommunityGuid: r._owningCommunityGuid,
        source: { type: 'role', data: r },
      });
    }

    // PROJECTS — full and mini-projects share this bucket; the typeTag
    // distinguishes them in the row meta. A project role is PREVIOUS (DEV-1368)
    // when its assignment is completed (from completedRoles) OR its project is
    // terminal — Completed/Abandoned, i.e. projectLookup row _isActiveProject
    // === false. Previous roles are hidden behind the "Show previous roles"
    // toggle in the rail.
    const projectRoleRows: Array<{ r: EnrichedRole; forcedPrev: boolean }> = [
      ...projectRoles.map((r) => ({ r, forcedPrev: false })),
      ...enrichedCompleted.map((r) => ({ r, forcedPrev: true })),
    ];
    for (const { r, forcedPrev } of projectRoleRows) {
      const isMini = r.engagementType === 'mini-project';
      const projRow = projectLookup.get(String(r.projectGuid || '').toUpperCase());
      const terminalProject = projRow ? projRow._isActiveProject === false : false;
      out.push({
        key: `role:${r.assignmentGuid}`,
        pillar: 'projects',
        roleLabel: r.roleLabel || r.roleCategory || '',
        entityName: r.projectName || t('engagements.unknownProject'),
        typeTag: isMini ? 'mini-project' : 'full-project',
        // DEV-1164 iter 3 cleanup — time commitment scrubbed; community
        // name kept as a useful disambiguation when present (helps tell
        // multiple same-named projects apart).
        secondary: r._communityName || '',
        isNew: isWithinLast3Days(r.assignedAt),
        heroUrl: r._heroUrl,
        heroSource: r._heroSource,
        logoUrl: null, // project/enabler roles use the square hero thumbnail
        owningCommunityName: r._owningCommunityName,
        owningCommunityGuid: r._owningCommunityGuid,
        isPrevious: forcedPrev || terminalProject,
        // Project ending dominates: if the project is terminal it's
        // "project_ended"; otherwise the user simply left a still-running role.
        previousKind: terminalProject
          ? 'project_ended'
          : forcedPrev
            ? 'role_ended'
            : undefined,
        source: { type: 'role', data: r },
      });
    }

    // ENABLERS — placeholder (enablerRoles is empty pending the Enablers
    // sources). Organizations are one Enabler entity type (typeTag
    // stays 'organization') and the on-behalf-of accessibility roles
    // will be another source type within the same Enabler pillar.
    // DEV-1164 iter 3 cleanup — secondary scrubbed (was r.timeCommitment).
    for (const r of enablerRoles) {
      out.push({
        key: `role:${r.assignmentGuid}`,
        pillar: 'enablers',
        roleLabel: r.roleLabel || r.roleCategory || '',
        entityName: r.projectName || '',
        typeTag: 'organization',
        secondary: '',
        isNew: isWithinLast3Days(r.assignedAt),
        heroUrl: r._heroUrl,
        heroSource: r._heroSource,
        logoUrl: null, // project/enabler roles use the square hero thumbnail
        owningCommunityName: r._owningCommunityName,
        owningCommunityGuid: r._owningCommunityGuid,
        source: { type: 'role', data: r },
      });
    }

    // COMMUNITIES + ENABLERS — community memberships split by entity type.
    // Geo entities (geo_*) → Communities pillar (white tint).
    // Org entities (org_*) → Enablers pillar (blue tint).
    // The pillar split was previously missing — every community engagement
    // landed in the Communities pillar regardless of underlying entity type
    // (DEV-1164 follow-up 2026-05-31, second sighting).
    for (const c of myCommunityEngagements) {
      // DEV-1164 follow-up (2026-06-01) — use the canonical predicate
      // from src/types/entityTypes.ts, which accepts both 'org_*' AND the
      // legacy 'organization' catch-all (per migration 127's "generic
      // catch-all" rationale, used by Rotary International, Life4Earth
      // Global, and other org roots). Replaces a stricter inline check
      // that missed the legacy value and silently classified those orgs
      // as geo Communities.
      const isOrg = isOrgEntityType(String(c.entityType || ''));
      out.push({
        // DEV-1368 — previous entries get a distinct key so a "Former" role
        // never collides with a same-named current role in the same community.
        key: `comm:${c.entityGuid.toUpperCase()}:${c.role}${c.isPrevious ? ':prev' : ''}`,
        pillar: isOrg ? 'enablers' : 'communities',
        roleLabel: capitalize(c.role),
        entityName: c.name,
        typeTag: isOrg ? 'organization' : 'community',
        secondary: '',
        isNew: false,
        heroUrl: c.heroUrl,
        heroSource: c.heroUrl ? 'own' : 'none',
        // DEV-1264 — circle avatar from the ancestor-cascaded logo, so a club
        // without its own logo inherits its root org's (Rotary International).
        logoUrl: c.logoUrl,
        // Geo community memberships have no "owning community" — the entity IS the community.
        owningCommunityName: null,
        owningCommunityGuid: null,
        // DEV-1368 — communities persist, so an ended community role is always
        // "role_ended" (never "project_ended"); drives the muted "Former" pill.
        isPrevious: c.isPrevious || undefined,
        previousKind: c.isPrevious ? 'role_ended' : undefined,
        source: { type: 'community', data: c },
      });
    }

    // SPEC_ROLE_ORGANIZED_INBOX §4a — attach the canonical community each entry
    // groups under in the Action Center rail: the entity itself for community/org
    // roles, the owning community for project/role entries, the workspace's
    // community for workspaces. Name resolved bare (not the breadcrumb path) via
    // communityLookup so the rail group header reads "Hampton Rotary Club".
    return out.map((e) => {
      let gGuid: string | null = null;
      if (e.typeTag === "community" || e.typeTag === "organization") {
        const src = e.source.type === "community" ? (e.source.data as CommunityEngagement) : null;
        gGuid = src ? String(src.entityGuid) : null;
      } else if (e.owningCommunityGuid) {
        gGuid = e.owningCommunityGuid;
      } else if (e.source.type === "workspace") {
        gGuid = (e.source.data as WorkspaceEngagement).communityGuid;
      }
      const gName = gGuid
        ? communityLookup.get(gGuid.toUpperCase())?.name ||
          e.owningCommunityName ||
          e.entityName ||
          null
        : null;
      return { ...e, groupCommunityGuid: gGuid, groupCommunityName: gName };
    });
    // capitalize/prettyPhase/isWithinLast3Days are stable closures; not deps.
    // t is included because labels resolve through it.
  }, [myWorkspaces, solutionRoles, projectRoles, enablerRoles, enrichedCompleted, projectLookup, myCommunityEngagements, communityLookup, t]);

  const loading = rolesLoading || miniProjectsLoading || wsLoading;

  return { entries: selectableEntries, loading };
}
